import React, {useState, useEffect, useRef} from "react";
import axios from "axios";
import {toast} from "react-toastify";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
  faArrowRightToBracket, faEye, faEyeSlash, faTruck, faLocationDot,
  faShieldHalved, faBell, faChartLine, faRoute, faUsers, faLock,
  faBluetooth, faSatelliteDish, faGaugeHigh, faClipboardCheck,
  faMapLocationDot, faCheckCircle, faXmark, faBars, faGlobe,
  faEnvelope, faPhone, faMobileScreenButton, faCalendarCheck,
} from "@fortawesome/free-solid-svg-icons";
import {faLinkedin, faInstagram, faXTwitter, faFacebook, faBluetoothB} from "@fortawesome/free-brands-svg-icons";
import * as Constants from "../utils/Constants";
import {identifierKind, normalizeIdentifier} from "../utils/identifier";
import {landingPathForRole} from "../utils/rolePermissions";
import logo from "../assets/yekkada-logo-new.png";
import "../styles/components/landing.css";

/* ── helpers ─────────────────────────────────────────── */
const useCountUp = (target, duration = 1800, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
};

/* ── shared post-login session handling ────────────────
   Used by both the password flow and the WhatsApp OTP flow below —
   both endpoints (`authenticate` and `verify-whatsapp-otp`) return the
   same LoginResponse shape. */
const applyLoginSession = (u) => {
  localStorage.setItem(Constants.LOGGED_IN_USER, JSON.stringify(u));
  localStorage.setItem(Constants.IS_AUTHENTICATED, true);
  localStorage.setItem("role", u.userRole || "");
  localStorage.setItem("client_name", u.clientName || "");
  // A Track Point driver's first-ever login (WhatsApp OTP, no password needed yet) comes back
  // with passwordSet: false — the account has no real password (see backend User.passwordSet).
  // Route to the mandatory one-time setup screen instead of their normal landing page; App.js's
  // PrivateRoute enforces the same check on every other protected route too, so this isn't the
  // only place that gate applies.
  if (u.passwordSet === false) {
    console.info("[Landing] Sign-in OK — no password set yet", {role: u.userRole});
    window.location.href = "/set-initial-password";
    return;
  }

  // Role-based landing (single source of truth in rolePermissions.js, shared with App.js's
  // route guard) — only Track Point (MOBI_TRACK) goes straight to Live Tracking; a client
  // employee lands on the Dashboard, scoped to their allocated assets.
  console.info("[Landing] Sign-in OK", {role: u.userRole});
  window.location.href = landingPathForRole(u.userRole);
};

/** Composes countryCode + subscriber without doubling the prefix
 *  (mirrors the Android OtpActivity.buildFullPhone helper). */
const buildFullPhone = (countryCode, phone) => {
  const cleanedPhone = phone.replace(/\D/g, "");
  const cleanedCountry = countryCode.trim().replace(/[^\d+]/g, "");
  const countryDigits = cleanedCountry.replace("+", "");
  if (cleanedPhone.startsWith(countryDigits) && countryDigits) {
    return cleanedPhone.startsWith("+") ? cleanedPhone : `+${cleanedPhone}`;
  }
  return (cleanedCountry.startsWith("+") ? cleanedCountry : `+${cleanedCountry}`) + cleanedPhone;
};

const OTP_RESEND_COOLDOWN_S = 60;

/* ── login form (reused in nav dropdown & mobile) ───── */
const LoginForm = ({onClose}) => {
  const [mode, setMode] = useState("password"); // "password" | "otp"

  // Password sign-in
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);

  // WhatsApp OTP sign-in
  const [otpStep, setOtpStep] = useState("phone"); // "phone" | "code"
  const [otpCountryCode, setOtpCountryCode] = useState("+91");
  const [otpPhone, setOtpPhone] = useState("");
  const [otpFullPhone, setOtpFullPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpResendIn, setOtpResendIn] = useState(0);

  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  // Surface errors as a prominent top-right toast (rendered at the app root, so it
  // survives the login dropdown unmounting when it closes) in addition to the inline text.
  const showErr = (m) => {
    setErr(m);
    // Also surface it as a prominent top-right toast that renders at the app root,
    // so the message is visible even though the login dropdown closes on submit.
    toast.error(m, {autoClose: 6000});
  };

  useEffect(() => {
    if (otpResendIn <= 0) return;
    const timer = setInterval(() => setOtpResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [otpResendIn]);

  const switchMode = (next) => {
    setMode(next);
    setErr(""); setInfo("");
  };

  const submitPassword = async () => {
    setErr("");
    if (!username || !password) { showErr("Username / email / phone and password are required."); return; }
    try {
      setLoading(true);
      // Multi-identifier sign-in (MoM point 13): normalised identifier in
      // the existing `username` field — backend looks up by username OR
      // email OR phone; identifierType is a free hint.
      const identifier = normalizeIdentifier(username);
      const kind = identifierKind(identifier);
      console.info("[Landing] Sign-in attempt", {identifierType: kind});
      const attempt = (id) => axios.post(
        Constants.BASE_URL + "api/auth/v1/authenticate",
        {username: id, password, identifierType: kind === "empty" ? "username" : kind}
      );
      let res;
      try {
        res = await attempt(identifier);
      } catch (firstErr) {
        // The backend lookup is case-sensitive. For emails, retry once
        // with the lower-cased form so 'Kumar@X.com' still signs in a
        // user stored as 'kumar@x.com' (and vice versa via as-typed).
        const lowered = identifier.toLowerCase();
        if (kind === "email" && lowered !== identifier && firstErr.response?.status) {
          console.debug("[Landing] Email sign-in retry with lower-cased identifier");
          res = await attempt(lowered);
        } else {
          throw firstErr;
        }
      }
      if (res.status === 200) applyLoginSession(res.data);
    } catch (e) {
      console.debug("[Landing] Sign-in failed", {status: e.response?.status ?? "network"});
      const data = e.response?.data;
      showErr(
        (typeof data === "string" && data.trim())
          ? data
          : (data?.responseMessage || data?.message || "Incorrect username or password.")
      );
    } finally {
      setLoading(false);
    }
  };

  const requestOtp = async () => {
    setErr(""); setInfo("");
    const digits = otpPhone.replace(/\D/g, "");
    if (!digits) { showErr("Enter your WhatsApp number."); return; }
    const fullPhone = buildFullPhone(otpCountryCode, digits);
    try {
      setLoading(true);
      console.info("[Landing] WhatsApp OTP requested");
      await axios.post(Constants.BASE_URL + "api/auth/v1/request-whatsapp-otp", {phoneNumber: fullPhone});
      setOtpFullPhone(fullPhone);
      setOtpStep("code");
      setOtpResendIn(OTP_RESEND_COOLDOWN_S);
      setInfo(`OTP sent over WhatsApp to ${fullPhone}.`);
    } catch (e) {
      showErr(e?.response?.data?.responseMessage || "Could not send OTP. Check the number and try again.");
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    if (otpResendIn > 0 || loading) return;
    setErr(""); setInfo("");
    try {
      setLoading(true);
      console.info("[Landing] WhatsApp OTP resend requested");
      await axios.post(Constants.BASE_URL + "api/auth/v1/request-whatsapp-otp", {phoneNumber: otpFullPhone});
      setOtpResendIn(OTP_RESEND_COOLDOWN_S);
      setInfo(`OTP resent over WhatsApp to ${otpFullPhone}.`);
    } catch (e) {
      showErr(e?.response?.data?.responseMessage || "Could not resend OTP. Try again later.");
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setErr("");
    if (!otpCode) { showErr("Enter the OTP you received."); return; }
    try {
      setLoading(true);
      const res = await axios.post(Constants.BASE_URL + "api/auth/v1/verify-whatsapp-otp", {phoneNumber: otpFullPhone, otp: otpCode});
      if (res.status === 200) applyLoginSession(res.data);
    } catch (e) {
      showErr(e?.response?.data?.responseMessage || "Invalid or expired OTP.");
    } finally {
      setLoading(false);
    }
  };

  const changeNumber = () => {
    setOtpStep("phone");
    setOtpCode("");
    setOtpResendIn(0);
    setErr(""); setInfo("");
  };

  const onKey = (e, fn) => { if (e.key === "Enter") fn(); };

  return (
    <div className="lp-login-form">
      {onClose && (
        <button className="lp-login-close" onClick={onClose} aria-label="Close">
          <FontAwesomeIcon icon={faXmark} />
        </button>
      )}
      <p className="lp-login-title">Welcome back</p>
      <p className="lp-login-sub">Sign in to your dashboard</p>

      <div className="lp-login-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "password"}
          className={`lp-login-tab ${mode === "password" ? "lp-login-tab--active" : ""}`}
          onClick={() => switchMode("password")}
        >
          Password
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "otp"}
          className={`lp-login-tab ${mode === "otp" ? "lp-login-tab--active" : ""}`}
          onClick={() => switchMode("otp")}
        >
          WhatsApp OTP
        </button>
      </div>

      {mode === "password" ? (
        <>
          <input
            className="lp-input"
            type="text"
            placeholder="Username, email, or phone"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyDown={(e) => onKey(e, submitPassword)}
            autoComplete="username"
          />
          <div className="lp-input-wrap">
            <input
              className="lp-input"
              type={showPw ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => onKey(e, submitPassword)}
              autoComplete="current-password"
            />
            <button className="lp-eye" onClick={() => setShowPw(!showPw)} tabIndex={-1}>
              <FontAwesomeIcon icon={showPw ? faEyeSlash : faEye} />
            </button>
          </div>
          {err && <p className="lp-login-err">{err}</p>}
          <button className="lp-btn-primary lp-btn-full" onClick={submitPassword} disabled={loading}>
            <FontAwesomeIcon icon={faArrowRightToBracket} />
            {loading ? "Signing in…" : "Sign In"}
          </button>
          <button
            type="button"
            className="lp-login-forgot"
            onClick={() => {
              console.info("[Landing] Forgot-password flow opened");
              window.location.href = "/forgot-password";
            }}
          >
            Forgot password?
          </button>
        </>
      ) : (
        <>
          {otpStep === "phone" ? (
            <>
              <div className="lp-phone-row">
                <input
                  className="lp-input lp-phone-cc"
                  type="tel"
                  placeholder="+91"
                  value={otpCountryCode}
                  onChange={(e) => setOtpCountryCode(e.target.value.replace(/[^\d+]/g, ""))}
                  aria-label="Country code"
                />
                <input
                  className="lp-input lp-phone-num"
                  type="tel"
                  inputMode="numeric"
                  placeholder="WhatsApp number"
                  value={otpPhone}
                  onChange={(e) => setOtpPhone(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => onKey(e, requestOtp)}
                  autoComplete="tel"
                  autoFocus
                />
              </div>
              {err && <p className="lp-login-err">{err}</p>}
              <button className="lp-btn-primary lp-btn-full" onClick={requestOtp} disabled={loading}>
                <FontAwesomeIcon icon={faMobileScreenButton} />
                {loading ? "Sending…" : "Send OTP via WhatsApp"}
              </button>
              <p className="lp-login-hint">We'll send a 6-digit code to this number on WhatsApp.</p>
            </>
          ) : (
            <>
              {info && <p className="lp-login-info">{info}</p>}
              <input
                className="lp-input"
                type="text"
                inputMode="numeric"
                placeholder="6-digit OTP"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => onKey(e, verifyOtp)}
                autoFocus
              />
              {err && <p className="lp-login-err">{err}</p>}
              <button className="lp-btn-primary lp-btn-full" onClick={verifyOtp} disabled={loading}>
                <FontAwesomeIcon icon={faArrowRightToBracket} />
                {loading ? "Verifying…" : "Verify & Sign in"}
              </button>
              <div className="lp-otp-actions">
                <button type="button" className="lp-login-link" onClick={resendOtp} disabled={loading || otpResendIn > 0}>
                  {otpResendIn > 0 ? `Resend OTP (${otpResendIn}s)` : "Resend OTP"}
                </button>
                <button type="button" className="lp-login-link" onClick={changeNumber} disabled={loading}>
                  Change number
                </button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

/* ── demo request modal ──────────────────────────────── */
const DemoModal = ({onClose}) => {
  const [form, setForm] = useState({name: "", email: "", company: "", phone: ""});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm((p) => ({...p, [k]: e.target.value}));

  const submit = async () => {
    if (!form.name || !form.email) { setErr("Name and email are required."); return; }
    setErr("");
    setStatus("loading");
    try {
      await axios.post(Constants.BASE_URL + "api/public/demo-request", form);
      setStatus("success");
    } catch (e) {
      setErr("Something went wrong. Please email us at support@yekkada.com");
      setStatus("error");
    }
  };

  return (
    <div className="lp-modal-backdrop" onClick={onClose}>
      <div className="lp-modal" onClick={(e) => e.stopPropagation()}>
        <button className="lp-login-close" onClick={onClose}><FontAwesomeIcon icon={faXmark}/></button>
        {status === "success" ? (
          <div className="lp-demo-success">
            <FontAwesomeIcon icon={faCheckCircle} className="lp-demo-success-icon"/>
            <h3>Request received!</h3>
            <p>Our team will reach out to you within one business day to schedule your demo.</p>
            <button className="lp-btn-primary" onClick={onClose}>Close</button>
          </div>
        ) : (
          <>
            <p className="lp-login-title">Request a Demo</p>
            <p className="lp-login-sub">Tell us about yourself and we'll set up a personalised walkthrough.</p>
            <input className="lp-input" placeholder="Full name *" value={form.name} onChange={set("name")}/>
            <input className="lp-input" type="email" placeholder="Work email *" value={form.email} onChange={set("email")}/>
            <input className="lp-input" placeholder="Company / Organisation" value={form.company} onChange={set("company")}/>
            <input className="lp-input" placeholder="Phone number (optional)" value={form.phone} onChange={set("phone")}/>
            {err && <p className="lp-login-err">{err}</p>}
            <button className="lp-btn-primary lp-btn-full" onClick={submit} disabled={status === "loading"}>
              <FontAwesomeIcon icon={faCalendarCheck}/>
              {status === "loading" ? "Sending…" : "Request Demo"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

/* ── data ────────────────────────────────────────────── */
const FEATURES = [
  {icon: faMapLocationDot, title: "Live Fleet Tracking", desc: "Real-time GPS position of every vehicle on an interactive map. Know where your fleet is, right now."},
  {icon: faRoute,          title: "Trip Route Replay",   desc: "Visualise every ignition session as a colour-coded polyline. Hover to see exact time & speed at any point."},
  {icon: faBell,           title: "Instant Alerts",      desc: "Overspeed, tamper, power-cut, and low-battery events push to WhatsApp and email within seconds."},
  {icon: faChartLine,      title: "Fleet Reports",       desc: "Daily activity, trip summaries, fleet distance, and alert logs — all exportable to Excel or PDF."},
  {icon: faMobileScreenButton, title: "Track Point",        desc: "Turn any smartphone into a GPS tracker. Field workers share live location from the Yekkada app — no hardware needed."},
  {icon: faShieldHalved,   title: "Secure Access",       desc: "Role-based permissions, full audit log, and Bluetooth command-and-control keep your data and assets safe."},
];

const PRODUCTS = [
  {
    icon: faLock,
    accent: "#363166",
    name: "GPS Lock",
    tagline: "Smart security with GPS proof",
    points: ["GPS-stamped lock/unlock log", "Task & asset tracking", "Remote status monitoring", "Full audit trail"],
  },
  {
    icon: faBluetoothB,
    accent: "#0284c7",
    name: "BT Lock",
    tagline: "Bluetooth-secured compartments",
    points: ["Phone-based unlock", "Full Bluetooth event history", "User access management", "Works offline"],
  },
  {
    icon: faTruck,
    accent: "#FF683A",
    name: "GPS Tracker",
    tagline: "Always know where your vehicle is",
    points: ["Live location + trip history", "Engine on/off detection", "Overspeed & tamper alerts", "30-day route replay"],
  },
  {
    icon: faMobileScreenButton,
    accent: "#16a34a",
    name: "Track Point",
    tagline: "Phone-based live tracking for field teams",
    points: ["Live GPS from any smartphone", "Route history & session replay", "No hardware required", "Real-time supervisor dashboard"],
  },
];

const HOW = [
  {step: "01", title: "Connect your devices", desc: "Plug in a GPS Tracker, mount a GPS Lock, commission a BT Lock, or simply install the Track Point app on your team's phones. Our onboarding team handles configuration."},
  {step: "02", title: "Add assets to Yekkada", desc: "Register your vehicles and devices in minutes. Group them by fleet, region, or driver for easy management."},
  {step: "03", title: "Track, report & act", desc: "Open the live dashboard for real-time location, dive into reports for historical analysis, or let automated alerts keep you informed."},
];

/* ── component ──────────────────────────────────────── */
const LandingPage = () => {
  const [loginOpen, setLoginOpen]   = useState(false);
  const [demoOpen, setDemoOpen]     = useState(false);
  const [menuOpen, setMenuOpen]     = useState(false);
  const [statsVisible, setStatsVisible] = useState(false);
  const [scrolled, setScrolled]     = useState(false);
  const statsRef = useRef(null);
  const loginRef = useRef(null);

  const vehicles = useCountUp(1200, 1600, statsVisible);
  const alerts   = useCountUp(850000, 1800, statsVisible);
  const clients  = useCountUp(180,   1400, statsVisible);
  const uptime   = useCountUp(99,    1200, statsVisible);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setStatsVisible(true); }, {threshold: 0.3});
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (loginRef.current && !loginRef.current.contains(e.target)) setLoginOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({behavior: "smooth"});
    setMenuOpen(false);
  };

  return (
    <div className="lp-root">
      {/* ── NAV ───────────────────────────────────── */}
      <nav className={`lp-nav ${scrolled ? "lp-nav--scrolled" : ""}`}>
        <div className="lp-nav-inner">
          <img src={logo} alt="Yekkada" className="lp-nav-logo" />

          <div className="lp-nav-links">
            <button onClick={() => scrollTo("features")}>Features</button>
            <button onClick={() => scrollTo("products")}>Products</button>
            <button onClick={() => scrollTo("how")}>How it works</button>
            <button onClick={() => scrollTo("contact")}>Contact</button>
          </div>

          <div className="lp-nav-actions" ref={loginRef}>
            <button className="lp-btn-primary lp-nav-login-btn" onClick={() => setLoginOpen(!loginOpen)}>
              <FontAwesomeIcon icon={faArrowRightToBracket} />
              Login
            </button>
            {loginOpen && (
              <div className="lp-nav-dropdown">
                <LoginForm onClose={() => setLoginOpen(false)} />
              </div>
            )}
          </div>

          <button className="lp-hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            <FontAwesomeIcon icon={menuOpen ? faXmark : faBars} />
          </button>
        </div>

        {menuOpen && (
          <div className="lp-mobile-menu">
            <button onClick={() => scrollTo("features")}>Features</button>
            <button onClick={() => scrollTo("products")}>Products</button>
            <button onClick={() => scrollTo("how")}>How it works</button>
            <button onClick={() => scrollTo("contact")}>Contact</button>
            <hr />
            <LoginForm />
          </div>
        )}
      </nav>

      {/* ── HERO ─────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-overlay" />
        <div className="lp-hero-content">
          <div className="lp-hero-badge">
            <FontAwesomeIcon icon={faSatelliteDish} />
            Fleet intelligence platform
          </div>
          <h1 className="lp-hero-h1">
            Get Trackking.<br />
            <span className="lp-hero-accent">Every vehicle.</span><br />
            Every moment.
          </h1>
          <p className="lp-hero-sub">
            Yekkada gives logistics teams live GPS tracking, automated trip reports,
            instant alerts, and Bluetooth lock control — all in one dashboard.
          </p>
          <div className="lp-hero-ctas">
            <button className="lp-btn-primary lp-btn-lg" onClick={() => setLoginOpen(true)}>
              <FontAwesomeIcon icon={faArrowRightToBracket} />
              Sign in to dashboard
            </button>
            <button className="lp-btn-outline lp-btn-lg" onClick={() => setDemoOpen(true)}>
              <FontAwesomeIcon icon={faCalendarCheck} />
              Request Demo
            </button>
          </div>
          <div className="lp-hero-devices">
            <span><FontAwesomeIcon icon={faTruck} /> GPS Tracker</span>
            <span><FontAwesomeIcon icon={faLock} /> GPS Lock</span>
            <span><FontAwesomeIcon icon={faBluetoothB} /> BT Lock</span>
          </div>
        </div>
        <div className="lp-hero-visual">
          <div className="lp-map-card">
            <div className="lp-map-header">
              <span className="lp-map-dot lp-dot-green" />
              Live tracking — 3 vehicles online
            </div>
            <div className="lp-map-body">
              <svg viewBox="0 0 320 200" className="lp-map-svg">
                <defs>
                  <linearGradient id="roadGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#363166" stopOpacity="0.15"/>
                    <stop offset="100%" stopColor="#FF683A" stopOpacity="0.15"/>
                  </linearGradient>
                </defs>
                {/* grid */}
                {[40,80,120,160].map(y=><line key={y} x1="0" y1={y} x2="320" y2={y} stroke="#e5e7eb" strokeWidth="0.5"/>)}
                {[64,128,192,256].map(x=><line key={x} x1={x} y1="0" x2={x} y2="200" stroke="#e5e7eb" strokeWidth="0.5"/>)}
                {/* roads */}
                <path d="M20 100 Q80 60 160 90 Q240 120 300 80" stroke="#d1d5db" strokeWidth="6" fill="none" strokeLinecap="round"/>
                <path d="M20 100 Q80 60 160 90 Q240 120 300 80" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeDasharray="8 6"/>
                <path d="M60 20 Q90 80 120 140 Q150 180 200 190" stroke="#d1d5db" strokeWidth="5" fill="none" strokeLinecap="round"/>
                <path d="M60 20 Q90 80 120 140 Q150 180 200 190" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeDasharray="6 5"/>
                {/* trip route */}
                <path d="M40 120 Q70 85 110 95 Q150 105 190 75 Q220 55 260 70" stroke="#FF683A" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                {/* vehicles */}
                <circle cx="190" cy="75" r="7" fill="#363166" stroke="white" strokeWidth="2"/>
                <circle cx="190" cy="75" r="12" fill="#363166" fillOpacity="0.15"/>
                <circle cx="110" cy="95" r="6" fill="#16a34a" stroke="white" strokeWidth="2"/>
                <circle cx="260" cy="70" r="6" fill="#FF683A" stroke="white" strokeWidth="2"/>
                {/* labels */}
                <rect x="195" y="63" width="52" height="16" rx="4" fill="#363166"/>
                <text x="221" y="75" textAnchor="middle" fill="white" fontSize="8" fontWeight="600">84 km/h</text>
              </svg>
            </div>
            <div className="lp-map-footer">
              <div className="lp-map-stat"><span className="lp-map-dot lp-dot-orange"/>Vehicle A · 84 km/h</div>
              <div className="lp-map-stat"><span className="lp-map-dot lp-dot-green"/>Vehicle B · Idle</div>
              <div className="lp-map-stat"><span className="lp-map-dot lp-dot-blue"/>Vehicle C · 52 km/h</div>
            </div>
          </div>
          <div className="lp-mini-cards">
            <div className="lp-mini-card">
              <FontAwesomeIcon icon={faBell} style={{color:"#FF683A"}}/>
              <div>
                <div className="lp-mini-label">Alert sent</div>
                <div className="lp-mini-val">Overspeed · 84 km/h</div>
              </div>
            </div>
            <div className="lp-mini-card">
              <FontAwesomeIcon icon={faRoute} style={{color:"#363166"}}/>
              <div>
                <div className="lp-mini-label">Today's trips</div>
                <div className="lp-mini-val">4 sessions · 133 km</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS ────────────────────────────────── */}
      <section className="lp-stats" ref={statsRef}>
        <div className="lp-container">
          <div className="lp-stats-grid">
            {[
              {val: vehicles, suffix: "+", label: "Vehicles tracked"},
              {val: alerts,   suffix: "+", label: "Alerts dispatched"},
              {val: clients,  suffix: "+", label: "Business clients"},
              {val: uptime,   suffix: "%", label: "Platform uptime"},
            ].map(({val, suffix, label}) => (
              <div key={label} className="lp-stat-item">
                <div className="lp-stat-num">{val.toLocaleString()}{suffix}</div>
                <div className="lp-stat-label">{label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURES ─────────────────────────────── */}
      <section id="features" className="lp-section lp-section--light">
        <div className="lp-container">
          <div className="lp-section-header">
            <span className="lp-eyebrow">What Yekkada does</span>
            <h2 className="lp-section-h2">Everything your fleet needs</h2>
            <p className="lp-section-sub">From the moment a vehicle starts its engine to the final report on your desk — Yekkada handles it.</p>
          </div>
          <div className="lp-features-grid">
            {FEATURES.map(({icon, title, desc}) => (
              <div key={title} className="lp-feature-card">
                <div className="lp-feature-icon"><FontAwesomeIcon icon={icon}/></div>
                <h3 className="lp-feature-title">{title}</h3>
                <p className="lp-feature-desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRODUCTS ─────────────────────────────── */}
      <section id="products" className="lp-section">
        <div className="lp-container">
          <div className="lp-section-header">
            <span className="lp-eyebrow">Hardware + software</span>
            <h2 className="lp-section-h2">Many products, one platform</h2>
            <p className="lp-section-sub">Mix and match devices across your fleet. All managed from the same Yekkada dashboard.</p>
          </div>
          <div className="lp-products-grid">
            {PRODUCTS.map(({icon, accent, name, tagline, points}) => (
              <div key={name} className="lp-product-card">
                <div className="lp-product-icon" style={{background: accent + "18", color: accent}}>
                  <FontAwesomeIcon icon={icon} />
                </div>
                <h3 className="lp-product-name">{name}</h3>
                <p className="lp-product-tagline">{tagline}</p>
                <ul className="lp-product-points">
                  {points.map(p => (
                    <li key={p}><FontAwesomeIcon icon={faCheckCircle} style={{color: accent}}/>{p}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────── */}
      <section id="how" className="lp-section lp-section--dark">
        <div className="lp-container">
          <div className="lp-section-header lp-section-header--light">
            <span className="lp-eyebrow lp-eyebrow--light">Simple setup</span>
            <h2 className="lp-section-h2 lp-h2--light">Up and running in three steps</h2>
          </div>
          <div className="lp-how-grid">
            {HOW.map(({step, title, desc}, i) => (
              <div key={step} className="lp-how-card">
                <div className="lp-how-step">{step}</div>
                {i < HOW.length - 1 && <div className="lp-how-connector"/>}
                <h3 className="lp-how-title">{title}</h3>
                <p className="lp-how-desc">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── INFOGRAPHIC / WHY ────────────────────── */}
      <section className="lp-section lp-section--light">
        <div className="lp-container">
          <div className="lp-why-grid">
            <div className="lp-why-text">
              <span className="lp-eyebrow">Why fleets choose Yekkada</span>
              <h2 className="lp-section-h2">Visibility that pays for itself</h2>
              <p className="lp-why-desc">Fleet managers who switch to Yekkada typically see measurable reductions in fuel wastage, unauthorised use, and incident response times — within the first month.</p>
              <div className="lp-why-stats">
                {[
                  {val: "23%", label: "Avg fuel saving"},
                  {val: "4×", label: "Faster incident response"},
                  {val: "91%", label: "Drivers report safer behaviour"},
                ].map(({val, label}) => (
                  <div key={label} className="lp-why-stat">
                    <div className="lp-why-stat-val">{val}</div>
                    <div className="lp-why-stat-label">{label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lp-why-visual">
              <svg viewBox="0 0 340 300" className="lp-infographic-svg">
                {/* donut chart */}
                <circle cx="170" cy="150" r="90" fill="none" stroke="#f3f4f6" strokeWidth="28"/>
                <circle cx="170" cy="150" r="90" fill="none" stroke="#363166" strokeWidth="28"
                  strokeDasharray="356 209" strokeDashoffset="89" strokeLinecap="round"/>
                <circle cx="170" cy="150" r="90" fill="none" stroke="#FF683A" strokeWidth="28"
                  strokeDasharray="100 465" strokeDashoffset="-267" strokeLinecap="round"/>
                <circle cx="170" cy="150" r="90" fill="none" stroke="#0284c7" strokeWidth="28"
                  strokeDasharray="60 505" strokeDashoffset="-367" strokeLinecap="round"/>
                {/* centre */}
                <circle cx="170" cy="150" r="58" fill="white"/>
                <text x="170" y="143" textAnchor="middle" fill="#363166" fontSize="22" fontWeight="800">99%</text>
                <text x="170" y="161" textAnchor="middle" fill="#6b7280" fontSize="10">Uptime</text>
                {/* legend */}
                <rect x="20" y="260" width="12" height="12" rx="3" fill="#363166"/>
                <text x="38" y="271" fill="#374151" fontSize="11">GPS Tracking</text>
                <rect x="120" y="260" width="12" height="12" rx="3" fill="#FF683A"/>
                <text x="138" y="271" fill="#374151" fontSize="11">Alerts</text>
                <rect x="200" y="260" width="12" height="12" rx="3" fill="#0284c7"/>
                <text x="218" y="271" fill="#374151" fontSize="11">BT Lock</text>
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* ── CONTACT ──────────────────────────────── */}
      <section id="contact" className="lp-section">
        <div className="lp-container">
          <div className="lp-section-header">
            <span className="lp-eyebrow">Get in touch</span>
            <h2 className="lp-section-h2">Ready to track your fleet?</h2>
            <p className="lp-section-sub">Speak to our team for a live demo or to get your first device configured.</p>
          </div>
          <div className="lp-contact-cards">
            <button className="lp-contact-card" onClick={() => setDemoOpen(true)}>
              <FontAwesomeIcon icon={faCalendarCheck}/>
              <span>Request a Demo</span>
            </button>
            <a href="mailto:support@yekkada.com" className="lp-contact-card">
              <FontAwesomeIcon icon={faEnvelope}/>
              <span>support@yekkada.com</span>
            </a>
            <a href="https://satyology.com" target="_blank" rel="noopener noreferrer" className="lp-contact-card">
              <FontAwesomeIcon icon={faGlobe}/>
              <span>satyology.com</span>
            </a>
          </div>
        </div>
      </section>

      {/* ── MOBILE APP ───────────────────────────── */}
      <section className="lp-section lp-section--dark">
        <div className="lp-container">
          <div className="lp-app-grid">
            <div className="lp-app-text">
              <span className="lp-eyebrow lp-eyebrow--light">Mobile app</span>
              <h2 className="lp-section-h2 lp-h2--light">Track your fleet from anywhere</h2>
              <p className="lp-app-desc">The Yekkada Android app puts live tracking, alerts, and lock control in your pocket — works seamlessly with your web dashboard.</p>
              <div className="lp-app-badges">
                <a
                  href="https://play.google.com/store/apps/details?id=com.satyology.yekkada"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lp-play-badge"
                >
                  <FontAwesomeIcon icon={faMobileScreenButton} className="lp-play-icon"/>
                  <div>
                    <div className="lp-play-sub">Get it on</div>
                    <div className="lp-play-name">Google Play</div>
                  </div>
                </a>
              </div>
            </div>
            <div className="lp-app-visual">
              <div className="lp-phone-mockup">
                <div className="lp-phone-screen">
                  <div className="lp-phone-status-bar"/>
                  <div className="lp-phone-content">
                    <div className="lp-phone-map-bg"/>
                    <div className="lp-phone-card">
                      <span className="lp-map-dot lp-dot-green"/>
                      <div>
                        <div style={{fontSize:"0.7rem",fontWeight:700,color:"#111"}}>Vehicle A</div>
                        <div style={{fontSize:"0.65rem",color:"#6b7280"}}>84 km/h · Moving</div>
                      </div>
                    </div>
                    <div className="lp-phone-card">
                      <span className="lp-map-dot lp-dot-orange"/>
                      <div>
                        <div style={{fontSize:"0.7rem",fontWeight:700,color:"#111"}}>Alert: Overspeed</div>
                        <div style={{fontSize:"0.65rem",color:"#6b7280"}}>Just now</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
            <div className="lp-footer-brand">
              <img src={logo} alt="Yekkada" className="lp-footer-logo"/>
              <p className="lp-footer-tagline">Get Trackking! — Real-time fleet visibility for every business.</p>
              <div className="lp-socials">
                <a href="https://www.linkedin.com/showcase/yekkada-fleet-tracking/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><FontAwesomeIcon icon={faLinkedin}/></a>
                <a href="https://www.instagram.com/Yekkada_get_tracking" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><FontAwesomeIcon icon={faInstagram}/></a>
                <a href="https://twitter.com/yekkada" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)"><FontAwesomeIcon icon={faXTwitter}/></a>
                <a href="https://facebook.com/yekkada" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><FontAwesomeIcon icon={faFacebook}/></a>
              </div>
            </div>
            <div className="lp-footer-col">
              <h4>Product</h4>
              <button onClick={() => scrollTo("features")}>Features</button>
              <button onClick={() => scrollTo("products")}>Products</button>
              <button onClick={() => scrollTo("how")}>How it works</button>
              <button onClick={() => setLoginOpen(true)}>Login</button>
            </div>
            <div className="lp-footer-col">
              <h4>Company</h4>
              <a href="https://satyology.com" target="_blank" rel="noopener noreferrer">Satyology Solutions</a>
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-and-conditions">Terms &amp; Conditions</a>
            </div>
            <div className="lp-footer-col">
              <h4>Support</h4>
              <a href="mailto:support@yekkada.com">support@yekkada.com</a>
              <a href="https://satyology.com" target="_blank" rel="noopener noreferrer">satyology.com</a>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <span>© 2023–{new Date().getFullYear()} Yekkada.com · Powered by{" "}
              <a href="https://satyology.com" target="_blank" rel="noopener noreferrer">Satyology Solutions Private Limited</a>
            </span>
            <div className="lp-footer-legal">
              <a href="/privacy-policy">Privacy</a>
              <a href="/terms-and-conditions">Terms</a>
            </div>
          </div>
        </div>
      </footer>

      {/* ── DEMO MODAL ───────────────────────────── */}
      {demoOpen && <DemoModal onClose={() => setDemoOpen(false)} />}
    </div>
  );
};

export default LandingPage;
